import type {
	TConversationTurnLoadFailure,
	TMessage,
	TTurnInfo,
} from "../types/agent";

import { getApiErrorMessage } from "./apiErrorUtils";

export const INJECTED_STEP_START_QUERY_TYPE =
	"injected_step_start_msg";

// Keep pending/error turns visible, but never render a completed empty turn as a timestamp-only bubble.
export const isVisiblePaulMessage = (
	message: TMessage
): boolean => {
	if (
		message.role === "user" &&
		message.query_type === INJECTED_STEP_START_QUERY_TYPE
	)
		return false;
	const hasContent =
		message.content.some(
			(part) =>
				part.content.trim().length > 0 ||
				Boolean(part.blob_link)
		) ||
		Boolean(
			message.attachments &&
				Object.keys(message.attachments).length > 0
		);
	return (
		hasContent ||
		(message.role === "model" &&
			Boolean(
				Boolean(message.error_msg) ||
					Boolean(message.turn_load_error) ||
					message.status === "IN_PROGRESS" ||
					message.status === "NOT_YET_STARTED"
			))
	);
};

type TLoadPaulTurnHistoryResult = {
	turns: Array<TTurnInfo>;
	failures: Array<TConversationTurnLoadFailure>;
	messages: Array<TMessage>;
};

export const buildPaulTurnLoadErrorMessage = (
	conversationId: string,
	turnIdx: number,
	error: string
): TMessage => ({
	msg_id: `turn-load-error-${conversationId}-${turnIdx}`,
	role: "model",
	turn_idx: turnIdx,
	time_millis: turnIdx,
	content: [],
	is_internal: false,
	attachments: null,
	refresh_sas_link_before_millis: null,
	error_msg: error,
	turn_load_error: true,
});

export const loadPaulTurnHistory = async ({
	conversationId,
	turnIndices,
	loadTurn,
}: {
	conversationId: string;
	turnIndices: Array<number>;
	loadTurn: (turnIdx: number) => Promise<TTurnInfo>;
}): Promise<TLoadPaulTurnHistoryResult> => {
	const results = await Promise.all(
		turnIndices.map(async (turnIdx) => {
			try {
				return {
					turnIdx,
					turn: await loadTurn(turnIdx),
					error: null,
				};
			} catch (error: unknown) {
				return {
					turnIdx,
					turn: null,
					error: getApiErrorMessage(
						error,
						"Paul could not load this turn."
					),
				};
			}
		})
	);

	const turns = results
		.flatMap((result) =>
			result.turn === null ? [] : [result.turn]
		)
		.sort(
			(turnA, turnB) => turnA.turn_idx - turnB.turn_idx
		);
	const failures = results
		.flatMap((result) =>
			result.error === null
				? []
				: [
						{
							turn_idx: result.turnIdx,
							error: result.error,
						},
					]
		)
		.sort(
			(failureA, failureB) =>
				failureA.turn_idx - failureB.turn_idx
		);
	const failedTurnMessages = failures.map((failure) =>
		buildPaulTurnLoadErrorMessage(
			conversationId,
			failure.turn_idx,
			failure.error
		)
	);

	return {
		turns,
		failures,
		messages: [
			...turns.flatMap((turn) => turn.messages),
			...failedTurnMessages,
		],
	};
};
